import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WIDGET_SRC = "https://assets.calendly.com/assets/external/widget.js";

function loadCalendly() {
  return new Promise((resolve, reject) => {
    if (window.Calendly) return resolve();
    let s = document.querySelector(`script[src="${WIDGET_SRC}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = WIDGET_SRC;
      s.async = true;
      document.body.appendChild(s);
    }
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", () => reject(new Error("widget")));
  });
}

function embedUrl(base) {
  const u = new URL(base);
  u.searchParams.set("hide_gdpr_banner", "1");
  u.searchParams.set("hide_event_type_details", "1");
  u.searchParams.set("background_color", "0b0f17");
  u.searchParams.set("text_color", "f8fafc");
  u.searchParams.set("primary_color", "d4af37");
  return u.toString();
}

export default function Book() {
  const host = useRef(null);
  const [config, setConfig] = useState(null);
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState("loading");
  const [booked, setBooked] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/booking`)
      .then(({ data }) => {
        setConfig(data);
        setSelected(data.event_types[0]?.scheduling_url || data.scheduling_url);
      })
      .catch(() => setState("error"));
  }, []);

  useEffect(() => {
    if (!selected || !host.current) return undefined;
    let disposed = false;
    setState("loading");
    loadCalendly()
      .then(() => {
        if (disposed || !host.current) return;
        host.current.replaceChildren();
        window.Calendly.initInlineWidget({ url: embedUrl(selected), parentElement: host.current, resize: true });
        setState("ready");
      })
      .catch(() => !disposed && setState("error"));
    return () => {
      disposed = true;
    };
  }, [selected]);

  useEffect(() => {
    const onMsg = (e) => {
      if (e.origin === "https://calendly.com" && e.data?.event === "calendly.event_scheduled") setBooked(true);
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, []);

  const eventTypes = config?.event_types || [];

  return (
    <div data-testid="book-page">
      <SEO
        title="Book a Call | Thrive Business Development"
        description="Choose a time for an initial conversation with Thrive Business Development about your market, target accounts and business development focus."
      />

      <section className="relative overflow-hidden pt-40 pb-12 sm:pt-48 sm:pb-16" data-testid="book-hero">
        <div className="absolute inset-0 dot-grid-bg opacity-50" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="eyebrow mb-6">Book a Call</p>
            <h1 className="font-display max-w-3xl text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-[-0.035em] leading-[1.02] text-white">
              Choose a Time That Suits You
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
              A short initial conversation about your market, your target clients and where sharper
              intelligence would make the biggest difference. No preparation required.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="book-calendar-section">
        <div className="grid gap-14 lg:grid-cols-[1fr_340px] lg:gap-20">
          <Reveal>
            {eventTypes.length > 1 && (
              <div className="mb-6 flex flex-wrap gap-3" data-testid="book-event-type-list">
                {eventTypes.map((e) => (
                  <button
                    key={e.scheduling_url}
                    type="button"
                    data-testid={`book-event-type-${e.duration}`}
                    onClick={() => setSelected(e.scheduling_url)}
                    className={`border px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                      selected === e.scheduling_url
                        ? "border-gold bg-gold/10 text-gold"
                        : "border-white/15 text-slate-400 hover:border-gold/60 hover:text-gold"
                    }`}
                  >
                    {e.name} · {e.duration} min
                  </button>
                ))}
              </div>
            )}

            <div className="relative border border-white/10 bg-navy-light/40" data-testid="book-calendar-frame">
              {state === "loading" && (
                <div className="absolute inset-0 z-10 flex items-center justify-center gap-3 bg-navy/60 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400" data-testid="book-loading">
                  <Loader2 size={15} className="animate-spin text-gold" /> Loading availability
                </div>
              )}
              {state === "error" ? (
                <div className="p-10" data-testid="book-error">
                  <p className="eyebrow mb-4">Calendar Unavailable</p>
                  <p className="max-w-md text-sm text-slate-400 leading-relaxed">
                    The booking calendar could not be loaded right now. You can send us a message
                    instead and we will respond directly to arrange a time.
                  </p>
                  <Link
                    to="/contact"
                    data-testid="book-fallback-contact-link"
                    className="mt-8 inline-flex items-center gap-2 border border-gold/50 px-6 py-3 text-[12px] font-semibold uppercase tracking-[0.18em] text-gold transition-colors hover:bg-gold hover:text-navy"
                  >
                    Contact Us <ArrowUpRight size={14} />
                  </Link>
                </div>
              ) : (
                <div ref={host} data-testid="book-calendly-widget" className="min-h-[760px] w-full" />
              )}
            </div>

            {booked && (
              <p className="mt-6 border-l-2 border-gold/70 pl-5 text-sm text-slate-300" data-testid="book-confirmed-note">
                Your call is booked. A calendar invitation is on its way to your inbox.
              </p>
            )}
          </Reveal>

          <Reveal delay={0.12}>
            <aside className="border border-white/10 bg-navy-light/40 p-8">
              <p className="eyebrow-blue mb-8">What to Expect</p>
              <ol className="space-y-7">
                {[
                  ["01", "A focused conversation about your offer, your sectors and where you want to grow."],
                  ["02", "An honest view on where market intelligence would sharpen your targeting."],
                  ["03", "Clear next steps if there is a fit. No pitch, no obligation."],
                ].map(([n, text]) => (
                  <li key={n} className="flex gap-4">
                    <span className="font-mono text-xs text-gold/70">{n}</span>
                    <span className="text-sm text-slate-400 leading-relaxed">{text}</span>
                  </li>
                ))}
              </ol>
              <div className="mt-10 border-t border-white/10 pt-6">
                <p className="text-xs leading-relaxed text-slate-500">
                  Prefer to write first?{" "}
                  <Link to="/contact" data-testid="book-contact-form-link" className="text-electric transition-colors hover:text-white">
                    Send us a message
                  </Link>{" "}
                  and we will respond directly.
                </p>
              </div>
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
